import confettiBell from "assets/images/confettiBell.svg"
import crownGold from "assets/images/crownGold.svg"
import { useGraphqlQuery } from "graphql-mock"
import { useModal } from "hooks"
import { IconButton, IconCertified, Modal } from "materia"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { displayTimestamp, exists } from "utils"
import { Avatar } from "../../../components/Avatar"
import { Stars } from "../../../components/Stars"
import { useGetOrdinal } from "../../../dataUtilities/getOrdinal"
import { usePageTitle } from "../../../tools/usePageTitle"
import { query, type Data, type RecentActivityEvent } from "./gql"
import { RecentActivityWidgetModalError } from "./RecentActivityWidgetModalError"
import { RecentActivityWidgetModalSkeleton } from "./RecentActivityWidgetModalSkeleton"

import s from "./RecentActivityWidgetModal.module.scss"

export const RecentActivityWidgetModal = ({
  closeRoute,
}: {
  closeRoute: string
}) => {
  const { t } = useTranslation()
  usePageTitle(t("pageTitle.RECENT_ACTIVITY"))

  const { onClose } = useModal(closeRoute)

  const { data, error, isPending, isSuccess } = useGraphqlQuery(query, {})

  return (
    <Modal onClose={onClose} ariaLabel="Recent activity">
      {isPending && <RecentActivityWidgetModalSkeleton />}
      {error && <RecentActivityWidgetModalError error={error} />}
      {isSuccess && <Content data={data} onClose={onClose} />}
    </Modal>
  )
}

const Content = ({ data, onClose }: { data: Data; onClose: () => void }) => {
  const { t } = useTranslation()

  const { completed, leaderboard, stars } = data

  const events = [completed, leaderboard, stars]
    .map((data) => data.nodes[0])
    .filter(exists)

  return (
    <div className={s.card}>
      <div className={s.card__top}>
        <div className={s.card__topText}>
          <img className={s.card__icon} src={confettiBell} alt="" />
          <h2 className={s.card__title}>{t("activityFeed.activity")}</h2>
        </div>
        <IconButton
          as="button"
          icon="Close"
          onClick={onClose}
          aria-label="Close"
        />
      </div>
      <ul className={s.card__entries}>
        {events.map((item, index) => (
          <Entry key={index} event={item} />
        ))}
      </ul>
    </div>
  )
}

const Entry = ({ event }: { event: RecentActivityEvent }) => {
  const { t, i18n } = useTranslation()
  const getOrdinal = useGetOrdinal()

  const { user, product, createdAt, eventType, rank, stars } = event

  const entryClass = modifiers(s, "entry", {
    orange: eventType === "leaderboard",
    green: eventType === "completed",
  })

  const getSummaryText = () => {
    if (eventType === "stars" && stars)
      return t("activityFeed.stars", { count: stars })
    if (eventType === "leaderboard" && rank) return getOrdinal(rank)
    return t("activityFeed.completed")
  }

  const getIcon = () => {
    if (eventType === "completed") {
      return <IconCertified />
    }
    if (eventType === "stars") {
      return <Stars count={1} max={1} size="extraSmall" />
    }
    if (eventType === "leaderboard") {
      return <img className={s.entry__icon} src={crownGold} alt="" />
    }
  }

  return (
    <li className={entryClass}>
      <Avatar alt={user.avatarTitle} image={user.avatar} size="medium" />

      <div className={s.entry__insightWrapper}>
        <div className={s.entry__topRow}>
          <span className={s.entry__name}>{user.displayName}</span>
          <span className={s.entry__time}>
            {displayTimestamp(createdAt, t, i18n.language)}
          </span>
        </div>

        <div className={s.entry__summary}>
          {getIcon()}
          <span className={s.entry__summaryText}>{getSummaryText()}</span>
          <span className={s.entry__product}>{product.title}</span>
        </div>
      </div>
    </li>
  )
}
